import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {
  NbComponentStatus,
  NbDialogRef,
  NbGlobalPhysicalPosition,
  NbToastrService,
} from "@nebular/theme";
import * as _ from "lodash";
import { Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { UserService } from "../../@core/services/user.service";
import { convertTimestamp } from "../../@core/utils/convertTimestamps.service";
import Exercise, { ExerciseUser } from "../../models/exercise.model";
import Quiz from "../../models/quiz.model";
import { ExerciseUserService } from "../../services/exercise-user.service";
import { NotificationService } from "../../services/notifications.service";

@Component({
  selector: "ngx-view-do-exercise",
  templateUrl: "view-do-exercise.component.html",
  styleUrls: ["view-do-exercise.component.scss"],
})
export class ViewDoExerciseComponent implements OnInit, OnDestroy {
  private destroy$: Subject<void> = new Subject<void>();
  @Input() exercise: Exercise = new Exercise();
  @Output() onMaxPercentage: EventEmitter<any> = new EventEmitter();

  exerciseUser?: ExerciseUser;
  exerciseUsers?: ExerciseUser[];
  exerciseHighest?: ExerciseUser;

  maxPercentage = 0;

  finishedDays = 0;
  finishedHours = 0;
  finishedMinutes = 0;
  finishedSeconds = 0;
  view = "info"; //quiz/info/finish/resume
  uid = "";

  submitted = false;
  loading = false;

  constructor(
    public userService: UserService,
    private exerciseUserService: ExerciseUserService,
    private toastrService: NbToastrService,
    private notiService: NotificationService
  ) {}

  ngOnInit() {
    this.userService
      .getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (user && user.uid != this.uid) {
          this.uid = user.uid;
          this.getExerciseUsers();
        } else {
          this.uid = "";
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  doExercise() {
    if (this.exerciseUser && this.exerciseUser.id == "") {
      this.view = "quiz";
    } else {
      this.createExerciseUser(1);
    }
  }

  resumeExercise() {
    this.view = "quiz";
  }

  replay() {
    //create new exerciseUser
    const noAttempt = this.exerciseUser.noAttempt++;
    this.createExerciseUser(noAttempt);
  }

  onSubmitted(value: Quiz) {
    this.updateExerciseSummary(value);
  }

  onFinished(value: ExerciseUser) {
    this.view = "finish";
    this.exerciseUser.timeSpent = 0;
    this.calculatorFinishedTime();
  }

  //ExerciseUser
  getExerciseUsers() {
    if (this.uid != "" && this.exercise.id != "") {
      this.loading = true;
      this.view = "info";

      this.exerciseUserService
        .getExerciseUsers(this.uid, this.exercise.id)
        .snapshotChanges(["added", "removed"])
        .pipe(
          map((changes) =>
            changes.map((c) => ({
              id: c.payload.doc.id,
              ...c.payload.doc.data(),
              createdOn: convertTimestamp(c.payload.doc.data().createdOn),
              updatedOn: convertTimestamp(c.payload.doc.data().updatedOn),
            }))
          ),
          takeUntil(this.destroy$)
        )
        .subscribe((data) => {
          this.exerciseUsers = data;

          if (data.length > 0) {
            this.exerciseUser = data[0];
            this.view =
              this.exerciseUser.noQuiz >= this.exerciseUser.totalQuiz
                ? "finish"
                : "resume";

            //Best score
            const finishedItems = _.filter(data, "finished");
            const maxItem = _.maxBy(finishedItems, "percentage");
            this.maxPercentage = maxItem ? maxItem.percentage : 0;
            const finishedSpeeds = _.filter(finishedItems, {
              percentage: this.maxPercentage,
            });
            this.exerciseHighest = _.sortBy(finishedSpeeds, ["timeSpent"])[0];
            this.onMaxPercentage.emit(this.maxPercentage);

            this.calculatorFinishedTime();

            _.remove(this.exerciseUsers, (e) => {
              return e.id == this.exerciseUser.id;
            });
          } else {
            this.exerciseUser = new ExerciseUser();
            this.exerciseHighest = new ExerciseUser();
            this.exerciseHighest.id = "";
            this.exerciseHighest.percentage = 0;
            this.exerciseHighest.timeSpent = 0;
            this.onMaxPercentage.emit(0);
          }

          console.log(
            "ExerciseComponent/getExerciseUsers/first:",
            this.exerciseUser
          );

          this.updateHighestExerciseUser();
          this.loading = false;
        });
    }
  }

  updateHighestExerciseUser() {
    if (this.uid != "" && this.exercise.id != "") {
      this.exerciseUserService
        .getHighestExerciseUser(this.uid, this.exercise.id)
        .snapshotChanges(["added", "removed"])
        .pipe(
          map((changes) =>
            changes.map((c) => ({
              id: c.payload.doc.id,
              ...c.payload.doc.data(),
              createdOn: convertTimestamp(c.payload.doc.data().createdOn),
              updatedOn: convertTimestamp(c.payload.doc.data().updatedOn),
            }))
          ),
          takeUntil(this.destroy$)
        )
        .subscribe((data) => {
          console.log("updateHighestExerciseUser", data);

          if (data.length > 0) {
            let isChanged = false;

            _.forEach(data, (oldHighest) => {
              if (oldHighest.id != this.exerciseHighest.id) {
                this.exerciseUserService.updateHighest(oldHighest.id, false);
                isChanged = true;
              }
            });

            if (isChanged) {
              this.exerciseUserService.updateHighest(
                this.exerciseHighest.id,
                true
              );
            }
          } else if (
            this.exerciseHighest &&
            this.exerciseHighest.id &&
            this.exerciseHighest.id != ""
          ) {
            this.exerciseUserService.updateHighest(
              this.exerciseHighest.id,
              true
            );
          }
        });
    }
  }

  private createExerciseUser(noAttempt) {
    if (this.exercise.status == 1 && this.exercise.exQuizzes.length > 0) {
      this.loading = true;
      this.submitted = true;
      this.exerciseUser = new ExerciseUser();

      //Lower security
      this.exerciseUser.noAttempt = noAttempt;
      this.exerciseUser.exerciseId = this.exercise.id;
      this.exerciseUser.percentCompleted = this.exercise.percentCompleted;
      this.exerciseUser.duration = this.exercise.duration;
      this.exerciseUser.isRepeated = this.exercise.isRepeated;
      this.exerciseUser.startDate = this.exercise.startDate;
      this.exerciseUser.endDate = this.exercise.endDate;
      this.exerciseUser.exQuizzes = _.shuffle(this.exercise.exQuizzes);

      this.exerciseUserService
        .createExercise(this.exerciseUser)
        .then((result) => {
          this.exerciseUser = result;
          console.log("createExerciseUser:", this.exerciseUser);

          this.view = "quiz";
          this.loading = false;
          this.submitted = false;
        })
        .catch((err) => {
          this.loading = false;
          this.submitted = false;
        });

      //Recommend for hight security
      // this.exerciseUserService
      //   .initExerciseUser(this.exercise.id)
      //   .pipe(takeUntil(this.destroy$))
      //   .subscribe((result) => {
      //     if (result) {
      //       this.exerciseUser = result;
      //       this.exerciseUser.createdOn = convertTimestamp(result.createdOn);

      //       console.log("createExerciseUser:", this.exerciseUser);

      //       this.view = "quiz";
      //     }
      //     this.loading = false;
      //     this.submitted = false;
      //   });
    }
  }

  private updateExerciseSummary(quiz: Quiz) {
    if (quiz.isCorrect) {
      this.exerciseUser.noCorrect++;
    } else {
      this.exerciseUser.noIncorrect++;
    }

    this.exerciseUser.noQuiz += 1;

    this.exerciseUser.finished =
      this.exerciseUser.noQuiz >= this.exerciseUser.exQuizzes.length;

    if (
      this.exerciseUser.noQuiz > 0 &&
      this.exerciseUser.exQuizzes.length > 0
    ) {
      this.exerciseUser.progress = Math.round(
        (this.exerciseUser.noQuiz / this.exerciseUser.exQuizzes.length) * 100
      );

      this.exerciseUser.percentage = Math.round(
        (this.exerciseUser.noCorrect / this.exerciseUser.exQuizzes.length) * 100
      );
    }

    if (this.exerciseUser.finished) {
      const dateNow = new Date();
      const timeSpent = Math.floor(
        dateNow.getTime() / 1000 - this.exerciseUser.createdOn.getTime() / 1000
      );
      this.exerciseUser.timeSpent = timeSpent;

      //Sync data
      this.exerciseUserService.sync(this.exerciseUser);
    }

    if (
      this.exerciseUser.finished &&
      this.exerciseUser.percentage > this.maxPercentage
    ) {
      this.maxPercentage = this.exerciseUser.percentage;

      //Send notification to author
      if (this.exercise.createdBy != this.uid) {
        this.notiService.create(
          "Làm bài",
          "exercise-users",
          this.exerciseUser.exerciseId,
          this.exerciseUser.id,
          this.exercise.title,
          `kết quả: ${this.exerciseUser.percentage}% / ${this.exerciseUser.timeSpent}s`,
          this.exercise.createdBy,
          `/exercise/${this.exercise.id}`,
          this.exercise.courseId ? this.exercise.courseId : ""
        );
      }
    }
  }

  private calculatorFinishedTime() {
    var day = 86400;
    var hour = 3600;
    var minute = 60;

    if (this.exerciseUser.timeSpent > 0) {
      const timeSpent = this.exerciseUser.timeSpent
        ? this.exerciseUser.timeSpent
        : 0;
      this.finishedDays = Math.floor(timeSpent / day);
      this.finishedHours = Math.floor(
        (timeSpent - this.finishedDays * day) / hour
      );
      this.finishedMinutes = Math.floor(
        (timeSpent - this.finishedDays * day - this.finishedHours * hour) /
          minute
      );
      this.finishedSeconds = Math.floor(
        timeSpent -
          this.finishedDays * day -
          this.finishedHours * hour -
          this.finishedMinutes * minute
      );

      if (
        !this.exerciseHighest ||
        this.exerciseHighest.percentage == 0 ||
        (this.exerciseUser.finished &&
          this.exerciseUser.percentage >= this.exerciseHighest.percentage &&
          this.exerciseUser.timeSpent < this.exerciseHighest.timeSpent)
      ) {
        this.exerciseHighest = this.exerciseUser;
        this.maxPercentage = this.exerciseHighest.percentage;
        this.onMaxPercentage.emit(this.maxPercentage);
      }
    }
  }

  private showToast(type: NbComponentStatus, title: string, body: string) {
    const config = {
      status: type,
      destroyByClick: true,
      duration: 2000,
      hasIcon: true,
      position: NbGlobalPhysicalPosition.TOP_RIGHT,
      preventDuplicates: false,
    };
    const titleContent = title ? `${title}` : "";

    this.toastrService.show(body, titleContent, config);
  }
}
